import React, { useState } from "react";
import axios from "axios";
import { CheckCircle2, XCircle } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Crumbs } from "../components/Shared";
import { useToast } from "../hooks/use-toast";
import { SITE } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#121214] border border-white/10 focus:border-[#9D0423] focus:outline-none focus:ring-1 focus:ring-[#9D0423]/40 text-white placeholder:text-neutral-600 px-4 py-3.5 text-sm transition-colors";

export default function MembershipCancellation() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", date: "", reason: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: "",
        message: `[Membership Cancellation Request] Requested cancellation date: ${form.date}. Reason: ${form.reason || "not given"}.`,
      });
      setDone(true);
    } catch (err) {
      toast({ title: "Something went wrong", description: `Please try again or email ${SITE.email}.` });
    } finally {
      setSending(false);
    }
  };

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Membership Cancellation", path: "/membership-cancellation-request" },
  ];

  return (
    <>
      <Seo
        title="Membership Cancellation Request | Boston Iron Grit"
        description="Request cancellation of your Boston Iron Grit membership. Submit your requested end date and we'll confirm by email."
        path="/membership-cancellation-request"
        noindex
      />
      <section className="bg-[#0A0A0B] py-14 lg:py-20 min-h-[70vh]">
        <div className="max-w-xl mx-auto px-6">
          <Reveal>
            <Crumbs items={crumbs} />
            <XCircle size={30} className="mt-8 text-[#9D0423]" />
            <h1 className="mt-4 font-heading font-extrabold uppercase text-4xl text-white tracking-tight">Membership cancellation request</h1>
            <p className="mt-4 text-neutral-400 text-[15px] leading-relaxed">
              Sorry to see you go. Submit the form and we'll process your cancellation per your membership agreement — and if a hold would
              serve you better, we'll suggest it.
            </p>
            {done ? (
              <div className="mt-10 p-7 bg-[#121214] border border-[#9D0423]/30" data-testid="cancel-success">
                <CheckCircle2 size={28} className="text-[#9D0423]" />
                <p className="mt-4 text-neutral-200 text-[15px]">
                  Request received. We'll confirm at <span className="text-white font-semibold">{form.email}</span> within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-4" data-testid="cancel-form">
                <input required value={form.name} onChange={set("name")} placeholder="Full name *" aria-label="Full name" data-testid="cancel-name" className={inputCls} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" aria-label="Email" data-testid="cancel-email" className={inputCls} />
                <div>
                  <label htmlFor="cancel-date" className="block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Requested cancellation date *</label>
                  <input id="cancel-date" required type="date" value={form.date} onChange={set("date")} data-testid="cancel-date" className={inputCls} />
                </div>
                <textarea rows={3} value={form.reason} onChange={set("reason")} placeholder="Reason (optional — it genuinely helps us improve)" aria-label="Reason" data-testid="cancel-reason" className={inputCls} />
                <button type="submit" disabled={sending} data-testid="cancel-submit" className="w-full bg-[#9D0423] hover:bg-[#7E031C] disabled:opacity-50 text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-6 py-4 transition-colors">
                  {sending ? "Sending…" : "Submit Cancellation Request"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
